import {
  createEstablishment,
  updateEstablishment,
  useEstablishment,
  useEstablishmentForm,
  useSellers,
} from '@gozoki/api'
import { Button, IconButton, Typography } from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useEffect, type FormEvent } from 'react'
import z from 'zod/v3'
import LoadingPaper from '../../../components/containers/LoadingPaper'
import SelectRhf from '../../../components/inputs/SelectRhf'
import TextFieldRhf from '../../../components/inputs/TextFieldRhf'
import Page from '../../../components/Page'
import { useRights } from '../../../utils/hooks'
import { ThemeColors } from '../../../utils/theme/colors.theme'
import { fonts } from '../../../utils/theme/fonts.theme'
import { ButtonOverrides } from '../../../utils/theme/overrides.theme'

export const Route = createFileRoute('/_authenticated/sellers/establishment/new')({
  component: AddEstablishmentPage,
  validateSearch: z.object({
    establishmentId: z.number().optional(),
  }),
})

function AddEstablishmentPage() {
  const navigate = Route.useNavigate()
  const { establishmentId } = Route.useSearch()

  const updateMode = !!establishmentId
  const isUserAdmin = useRights(['ADMIN'])

  // If this is update mode, use isLoading to show a loading indicator before the data arrives
  const { data, isLoading } = useEstablishment(establishmentId)

  const { data: sellers } = useSellers()

  const { control, trigger, getValues, setValue } = useEstablishmentForm()

  // Fill the form with initial data once the data is fetched
  useEffect(() => {
    if (data?.label) {
      setValue('label', data?.label)
    }
    if (data?.reference) {
      setValue('reference', data?.reference)
    }
    if (data?.address?.address) {
      setValue('address', data?.address.address)
    }
    if (data?.address?.addressAdditionnal) {
      setValue('addressAdditionnal', data?.address.addressAdditionnal.toString())
    }
    if (data?.address?.postalCode) {
      setValue('postalCode', data?.address.postalCode)
    }
    if (data?.address?.city) {
      setValue('city', data?.address.city)
    }
    if (data?.seller?.id) {
      setValue('sellerId', data?.seller.id)
    }
  }, [
    setValue,
    data?.label,
    data?.reference,
    data?.address?.address,
    data?.address?.addressAdditionnal,
    data?.address?.postalCode,
    data?.address?.city,
    data?.seller?.id,
  ])

  const navigateBack = () => navigate({ search: { tab: 'establishments' }, to: '/sellers' })

  const { mutate: upload, isPending: isUploading } = useMutation({
    mutationFn: createEstablishment,
    onSuccess: navigateBack,
  })

  const { mutate: update, isPending: isUpdating } = useMutation({
    mutationFn: updateEstablishment,
    onSuccess: navigateBack,
  })

  const uploadEstablishment = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const valid = await trigger() // Trigger form validation
    if (valid) {
      upload(getValues())
    }
    // If invalid, the invalid fields will be outlined with red
  }

  const uploadAndUpdateEstablishment = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const valid = await trigger()
    if (valid && establishmentId) {
      update({
        data: getValues(),
        id: establishmentId,
      })
    }
  }

  return (
    <Page sx={{ margin: 'auto', maxWidth: '50vw' }}>
      <form onSubmit={updateMode ? uploadAndUpdateEstablishment : uploadEstablishment}>
        <div style={{ alignItems: 'center', display: 'flex' }}>
          <IconButton onClick={navigateBack}>
            <ArrowLeft
              style={{
                color: ThemeColors.BLACK,
                height: '24px',
                width: '24px',
              }}
            />
          </IconButton>
          <Typography style={{ ...fonts.pageSubtitle, marginLeft: '16px' }} flexGrow={1}>
            {updateMode
              ? `${isUserAdmin ? 'Modifier' : 'Voir'} un établissement`
              : `Ajouter un établissement`}
          </Typography>

          <Button
            variant="outlined"
            sx={{ marginRight: '16px', ...ButtonOverrides.blackOutlined }}
            onClick={navigateBack}
          >
            Annuler
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!isUserAdmin ? true : isUploading || isUpdating}
          >
            {updateMode ? 'Modifier' : 'Ajouter'}
          </Button>
        </div>
        <LoadingPaper
          sx={{ marginTop: '40px', padding: '24px', paddingBottom: '40px' }}
          loading={isLoading && updateMode}
        >
          <Typography mb="8px" style={fonts.inputHeader}>
            Libellé
          </Typography>
          <TextFieldRhf
            variant="outlined"
            label="Libellé de l'établissement"
            control={control}
            trigger={trigger}
            name="label"
            fullWidth
          />
          <Typography mt="24px" mb="8px" style={fonts.inputHeader}>
            Référence
          </Typography>
          <TextFieldRhf
            variant="outlined"
            label="Référence de l'établissement"
            control={control}
            trigger={trigger}
            name="reference"
            fullWidth
          />
          <Typography mt="24px" mb="8px" style={fonts.inputHeader}>
            Vendeur
          </Typography>
          <SelectRhf
            label="Vendeur"
            control={control}
            name="sellerId"
            options={sellers?.map((s) => ({ label: s.label, value: s.id })) ?? []}
            required
          />
          <Typography mt="24px" mb="8px" style={fonts.inputHeader}>
            Addresse
          </Typography>
          <TextFieldRhf
            variant="outlined"
            label="Addresse de l'établissement"
            control={control}
            trigger={trigger}
            name="address"
            fullWidth
          />
          <Typography mt="24px" mb="8px" style={fonts.inputHeader}>
            Addresse (additionnelle)
          </Typography>
          <TextFieldRhf
            variant="outlined"
            label="Addresse de l'établissement (additionnelle)"
            control={control}
            trigger={trigger}
            name="addressAdditionnal"
            fullWidth
          />
          <Typography mt="24px" mb="8px" style={fonts.inputHeader}>
            Code postal
          </Typography>
          <TextFieldRhf
            variant="outlined"
            label="Code postal de l'établissement"
            control={control}
            trigger={trigger}
            name="postalCode"
            fullWidth
          />
          <Typography mt="24px" mb="8px" style={fonts.inputHeader}>
            Ville
          </Typography>
          <TextFieldRhf
            variant="outlined"
            label="Ville de l'établissement"
            control={control}
            trigger={trigger}
            name="city"
            fullWidth
          />
        </LoadingPaper>
      </form>
    </Page>
  )
}
